export const buttonStyles = {
  primary:
    "sz-btn sz-btn-primary inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-neutral-200 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",

  secondary:
    "sz-btn sz-btn-secondary inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-neutral-800 px-4 py-2 text-sm font-medium text-neutral-100 transition-colors hover:bg-neutral-700 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",

  subtle:
    "sz-btn sz-btn-subtle inline-flex items-center justify-center gap-2 rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-medium text-neutral-400 transition-colors hover:bg-white/5 hover:text-white",

  danger:
    "sz-btn sz-btn-danger inline-flex items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20",

  icon:
    "sz-btn-icon inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-neutral-400 transition-colors hover:bg-white/10 hover:text-white"
};

export const tabStyles = {
  container:
    "sz-tabs flex w-full min-w-0 items-center gap-1 overflow-x-auto border-b border-white/10 no-scrollbar",

  tab:
    "sz-tab inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",

  active:
    "active border-white text-white bg-transparent",

  inactive:
    "border-transparent text-neutral-400 hover:text-white",

  badgeActive:
    "sz-tab-badge rounded-full border border-white/15 bg-white/10 px-2 py-0.5 text-xs text-white",

  badgeInactive:
    "sz-tab-badge rounded-full border border-white/10 bg-neutral-800 px-2 py-0.5 text-xs text-neutral-400"
};

export const layoutStyles = {
  page: "sz-page-container space-y-6 max-w-7xl mx-auto w-full",
  header: "sz-page-header flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/10 pb-4",
  headerInfo: "sz-page-header-info flex flex-col gap-1",
  headerActions: "sz-page-header-actions flex items-center gap-2 flex-wrap w-full sm:w-auto",
  title: "sz-page-title text-xl sm:text-2xl font-semibold text-white tracking-tight",
  subtitle: "sz-page-subtitle text-xs text-neutral-400 font-mono",
  tabs: tabStyles.container,
  tab: tabStyles.tab,
  card: "glass-card p-5 rounded-2xl",
};

export const synczButtonStyles = {
  primary: buttonStyles.primary,
  secondary: buttonStyles.secondary,
  subtle: buttonStyles.subtle,
  danger: buttonStyles.danger,
  icon: buttonStyles.icon,
  ghost: buttonStyles.subtle,
  outline: buttonStyles.secondary,
  tab: tabStyles.tab,
  nav: "flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-medium text-neutral-400 hover:text-white hover:bg-white/5 transition-colors group w-full",
  toggle: "inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors"
};

export type ButtonVariant = keyof typeof synczButtonStyles | keyof typeof buttonStyles;

export const synczstyle = (variant: ButtonVariant = 'primary', additionalClasses: string = '') => {
  let base = (synczButtonStyles as any)[variant] || (buttonStyles as any)[variant] || buttonStyles.primary;
  
  const additionalTokens = additionalClasses.trim().split(/\s+/).filter(Boolean);
  const baseTokens = base.trim().split(/\s+/).filter(Boolean);
  
  const hasSolidBgWhite = additionalTokens.includes('bg-white') || baseTokens.includes('bg-white');
  const hasExplicitDarkText = additionalTokens.some((t: string) => ['text-black', 'text-neutral-900', 'text-zinc-900', 'text-gray-900', '!text-black'].includes(t));

  if (hasSolidBgWhite) {
    base = base
      .replace(/\b(bg-neutral-\d+|bg-zinc-\d+|bg-black|bg-transparent|hover:bg-neutral-\d+)\b/g, '')
      .replace(/\b(text-white|text-neutral-\d+|text-zinc-\d+|hover:text-white)\b/g, '');

    if (!hasExplicitDarkText) {
      additionalClasses = `text-black ${additionalClasses}`;
    }
  } else if (hasExplicitDarkText) {
    base = base
      .replace(/\b(text-white|text-neutral-\d+|text-zinc-\d+|hover:text-white)\b/g, '');
  }

  return `${base} ${additionalClasses}`.replace(/\s+/g, ' ').trim();
};

export const getTabClass = (isActive: boolean, additionalClasses: string = '') => {
  return `${tabStyles.tab} ${isActive ? tabStyles.active : tabStyles.inactive} ${additionalClasses}`.replace(/\s+/g, ' ').trim();
};
